import { StrictMode, useState, useEffect } from "react";
import { createRoot } from "react-dom";

import { Flipped, Flipper } from "react-flip-toolkit";

import Card from "./card.jsx";
import { FilterMenu } from "./grid-filter-menu.jsx";

const App = () => {
  const { cards, menus } = window.filterData;
  const menuItems = menus[0].items;

  /**
   * pre-filter cards based on the first item of the first menu
   */
  const firstTerm = menuItems[0].object_slug;
  const [filteredCards, setFilteredCards] = useState(
    cards.filter((card) => card.terms.includes(firstTerm)),
  );
  const [filteredTerms, setFilteredTerms] = useState([firstTerm]);
  const [current, setCurrent] = useState(window.location.hash);

  const subscribeForm = (
    <div
      key="subscribe-form"
      className="js--all-grid-items subscribe subscribe-default global-spacing--component"
      dangerouslySetInnerHTML={{ __html: window.filterData.subscribeForm }}
    />
  );

  const updater = () => setCurrent(window.location.hash);

  /**
   * Add popstate event listeners
   */
  useEffect(() => {
    // console.log("🟩 adding popstate listener");
    window.addEventListener("popstate", updater);

    return () => {
      // console.log("🔴 removing popstate listener");
      window.removeEventListener("popstate", updater);
    };
  }, []);

  /**
   * Update filteredCards on changes to current or cards
   */
  useEffect(() => {
    /**
     * Clean up hash or return the first item in the menus for an empty hash
     */
    const ft = current.length ? [current.slice(1)] : [firstTerm];

    /**
     *  special case for `#all` hash
     */
    const fc =
      current !== "#all"
        ? cards
            .filter((card) => ft.every((t) => card.terms.includes(t)))
            .sort((a, b) => {
              let order;
              for (let n = 0; n < ft.length; n++) {
                order = a.order[ft[n]] - b.order[ft[n]];
                if (order !== 0) {
                  break;
                }
              }
              if (order === 0) {
                order = b.date[ft[n]] - a.date[ft[n]];
              }
              return order ?? 0;
            })
        : cards.slice();

    setFilteredTerms(ft);
    setFilteredCards(fc);
  }, [cards, current]);

  const onAppear = (el, i) => {
    const duration = 250;
    setTimeout(
      () => {
        el.classList.add("fadein");
        el.style.animationDuration = `${duration}ms`;

        setTimeout(() => {
          el.style.opacity = 1;
          el.classList.remove("fadein");
        }, duration);
      },
      Math.min(Math.pow(i / 5, 0.7), 1) * 50,
    );
  };

  const onExit = (el, i, removeElement) => {
    const duration = 125;
    setTimeout(
      () => {
        el.classList.add("fadeout");
        el.style.animationDuration = `${duration}ms`;
        setTimeout(removeElement, duration);
      },
      /**
       * TODO: @codrin, change this timeout to something huge, like 10 seconds.
       * When changing visible filters, a second scrollbar appears on <body>
       * Seems to be related to this `overflow-x: hidden` rule: sass/base/_base.scss:36
       */
      // 10000
      Math.min(Math.pow(i / 5, 0.7), 1) * 50,
    );
  };

  const flipKey = filteredCards.map((c) => c.id).join();

  const renderedCards = filteredCards.map((card) => (
    <Flipped
      key={card.id}
      flipId={"cards" + card.id}
      stagger={true}
      onExit={onExit}
      onAppear={onAppear}
    >
      <Card
        style={{ width: 60 }}
        card={card}
        selection={{ primary: "", secondary: [] }}
      />
    </Flipped>
  ));

  /**
   * splice subscribe form into renderedCards
   */
  // renderedCards.splice(4, 0, subscribeForm);

  return (
    <div>
      <FilterMenu items={menuItems} current={current} />
      <Flipper
        flipKey={flipKey}
        spring={{ stiffness: 220, damping: 22 }}
        staggerConfig={{ default: { speed: 5 } }}
      >
        <section>
          <div className="grid">
            <div id="js--filter-grid" className="row cards">
              {renderedCards}
            </div>
          </div>
        </section>
      </Flipper>
    </div>
  );
};

/**
 * TODO: re-implement this
 */
const noCardMessage = (
  <div
    className="card col-12 col-md-6 js-ga-track-links"
    data-ga-action="Filter"
    data-ga-label="Reset Filters"
  >
    <h4>
      Nothing found. <a href="#">Clear selections</a>
    </h4>
  </div>
);

const container = document.getElementById("grid-wrapper");
if (container) {
  const root = createRoot(container);
  /**
   * StrictMode?
   * @link https://react.dev/reference/react/StrictMode
   */
  root.render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
  // root.render(<App />);
}
